import {
  STATION_1,
  STATION_2,
  STATION_3,
  STATION_4,
  WARM_UP,
} from 'utils/constants/stations';
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { messageError, messageSuccess } from 'views/ui/toast-message';
import {
  resetStations,
  setStations,
  visitAllStations,
} from 'modules/stations/actions';

import { ALL_LOCATIONS } from 'utils/constants/locations';
import SDK from 'utils/sdk';
import { SubmissionError } from 'redux-form';
import { push } from 'connected-react-router';
import { transformIncomingLocationStationsToLocal } from 'utils/location-stations-transform';
import {
  searchLocationStation,
  setLocation,
  setLocationStationDate,
  videoConsole,
} from './actions';
import {
  getLocation,
  getLocationStationDate,
  getLocationStationId,
} from './selectors';

export function* videoConsoleSaga({ payload: { values } }) {
  try {
    yield put(videoConsole.request());
    const {
      location: locationId,
      times: { exerciseTime, restTime },
      stations,
    } = values;

    // Select date and copy without reference (avoid date mutation)
    const date = new Date((yield select(getLocationStationDate)).getTime());

    const id = yield select(getLocationStationId);

    const requestBody = {
      date: date.toISOString(),
      location: locationId === ALL_LOCATIONS ? null : locationId,
      exercise_time: exerciseTime,
      rest_time: restTime,
      stations: {
        station_0: stations[WARM_UP]._id,
        station_1: {
          video_1: stations[STATION_1].A._id,
          video_2: stations[STATION_1].B._id,
          video_3: stations[STATION_1].C._id,
        },
        station_2: {
          video_1: stations[STATION_2].A._id,
          video_2: stations[STATION_2].B._id,
          video_3: stations[STATION_2].C._id,
        },
        station_3: {
          video_1: stations[STATION_3].A._id,
          video_2: stations[STATION_3].B._id,
          video_3: stations[STATION_3].C._id,
        },
        station_4: {
          video_1: stations[STATION_4].A._id,
          video_2: stations[STATION_4].B._id,
          video_3: stations[STATION_4].C._id,
        },
      },
    };

    const client = yield call([SDK, 'getClient']);
    let fetchedLocationStation = null;
    if (!id) {
      const { data } = yield call(
        [client, 'locationStationCreate'],
        null,
        requestBody
      );
      fetchedLocationStation = data;
    } else {
      const { data } = yield call(
        [client, 'locationStationUpdate'],
        { id },
        requestBody
      );
      fetchedLocationStation = data;
    }

    yield put(setStations(stations));
    yield put(
      videoConsole.success({
        id: fetchedLocationStation._id,
        exerciseTime,
        restTime,
      })
    );
    messageSuccess('The data has been saved correctly!');

    yield put(push('/app/video-console'));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(
      videoConsole.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
    messageError(data);
  } finally {
    yield put(videoConsole.fulfill());
  }
}

export function* searchLocationStationSaga() {
  try {
    yield put(searchLocationStation.request());

    const date = new Date((yield select(getLocationStationDate)).getTime());
    date.setUTCHours(0, 0, 0, 0);
    const location = yield select(getLocation);

    const client = yield call([SDK, 'getClient']);
    // Single request: when a location is selected, the API falls back to the
    // all-locations station for the date if the location has none.
    const locationStationsResponse = yield call(
      [client, 'locationStationListSearch'],
      {
        date: date.toISOString(),
        ...(location
          ? { location_id: location._id, include_fallback: true }
          : {}),
      }
    );

    const locationStations = locationStationsResponse.data.map(
      (locationStationResponse) => locationStationResponse.stations
    );

    let locationStationId = null;
    let locationStationExerciseTime = null;
    let locationStationRestTime = null;
    if (locationStations.length === 1) {
      // Videos are embedded in the location station, no extra request needed
      yield put(
        setStations(
          transformIncomingLocationStationsToLocal(locationStations)[0]
        )
      );

      const [locationStation] = locationStationsResponse.data;
      // A fallback (all-locations) station must not be updated when editing a
      // specific location: a null id makes the save create a new one.
      const isFallback = location !== null && !locationStation.location;
      locationStationId = isFallback ? null : locationStation._id;
      locationStationExerciseTime = locationStation.exercise_time;
      locationStationRestTime = locationStation.rest_time;

      yield put(visitAllStations());
    } else if (locationStations.length === 0) {
      yield put(resetStations());
    }

    yield put(
      searchLocationStation.success({
        id: locationStationId,
        exerciseTime: locationStationExerciseTime,
        restTime: locationStationRestTime,
      })
    );
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    // Don't leave the previous location's videos on screen
    yield put(resetStations());
    yield put(
      searchLocationStation.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
    messageError(data || 'Could not load the stations for this date');
  } finally {
    yield put(searchLocationStation.fulfill());
  }
}

export default function* locationStationWatch() {
  yield takeLatest(videoConsole.TRIGGER, videoConsoleSaga);

  // One watcher so any new trigger cancels the in-flight search
  yield takeLatest(
    [
      searchLocationStation.TRIGGER,
      setLocation.toString(),
      setLocationStationDate.toString(),
    ],
    searchLocationStationSaga
  );
}
